import React, { useState, useCallback } from 'react'

import Modal from 'components/Modal'
import TextInput from 'components/TextInput'

import Form from 'pages/shared/Form'
import ErrorMessage from 'components/ErrorMessage'

import Api from 'services/api'
import styled from 'styled-components'

import { isDeviceIdValid } from 'helpers/validations'

import Apptentive from 'helpers/apptentive'

type Props = {
  dealerId: number
  customerId: string
  onSaveCallback: (dealerId: string) => void
  closeCallback: () => void
}

const defaultDevice = {
  isNew: true,
  deviceType: 'thermostat',
  deviceId: '',
  note: null
}

const DEALER_MISMATCH_CODE = 429
const DEVICE_ALREADY_EXISTS_CODE = 415
const DEALER_MISMATCH_MSG = 'THIS DEVICE BELONGS TO ANOTHER DEALER. ' +
                             'The dealer code entered on the device is different from your dealer code. ' +
                             'Please contact support for assistance at 844-359-0270'
const DEVICE_ALREADY_EXISTS_MSG = 'This device is already assigned in your account. Show options for '

const AssignDevice = ({ dealerId, customerId, onSaveCallback, closeCallback } : Props) => {
  const [deviceId, setDeviceId] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState('')
  const [linkUrl, setLinkUrl] = useState('')
  const [linkDisplay, setLinkDisplay] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  const onCancel = () => {
    if (deviceId.length || note.length) {
      if (confirm('Do you really want to cancel?')) {
        closeCallback()
      }
    } else {
      closeCallback()
    }
  }

  const onSave = useCallback(async () => {
    if (isDeviceIdValid(deviceId)) {
      setIsSaving(true)

      const modifiedDevice = { ...defaultDevice, ...{ deviceId, note, dealerId, customerId } }
      await Api.Device.create(modifiedDevice).then(() => {
        Apptentive.sendEvent('assign_device_saved')

        onSaveCallback(deviceId)
        closeCallback()
      }).catch(async (error) => {
        const errorResponse = await error.response.json()
        let deviceError = ''
        if (errorResponse.code) {
          deviceError = errorResponse.code[0].name.toString()
        } else {
          deviceError = errorResponse.base[0].name
        }

        if (error.response.status === DEVICE_ALREADY_EXISTS_CODE) {
          setError(DEVICE_ALREADY_EXISTS_MSG)
          setLinkDisplay(deviceId)
          setLinkUrl(`/customers?page=1&sortAttribute=customer&sortDirection=asc&q=${deviceId}`)
        } else if (error.response.status === DEALER_MISMATCH_CODE) {
          setError(DEALER_MISMATCH_MSG)
          setLinkDisplay('')
          setLinkUrl('')
        } else {
          setError(deviceError.toString())
          setLinkDisplay('')
          setLinkUrl('')
        }
      }).finally(() => {
        setIsSaving(false)
      })
    } else {
      setError('Invalid Device AUID or Serial Number')
      setLinkDisplay('')
      setLinkUrl('')
    }
  }, [customerId, dealerId, deviceId, note, closeCallback, onSaveCallback])

  return (
    <Modal isDisplayed closeCallback={closeCallback} variant="sm" title="Assign System">
      <S.Form isSaving={isSaving} onSaveCallback={onSave} onCancelCallback={onCancel} noBorder>
        <TextInput label="Device AUID or Serial Number" value={deviceId} onChange={(event) => setDeviceId(event.target.value.toUpperCase())} />
        <S.ErrorMessage errorMessage={error} linkDisplay={linkDisplay} linkUrl={linkUrl} />
        <TextInput label="Notes" maxLength={250} value={note} onChange={(event) => setNote(event.target.value)} />
      </S.Form>
    </Modal>
  )
}

export default AssignDevice

const S = {
  ErrorMessage: styled(ErrorMessage)`
    margin-top: -10px;
    line-height: 15px;
    margin-bottom: 10px;
  `,
  Form: styled(Form)`
    > Fieldset{
      padding: 0px;
    }
  `
}
